/*
  Getting messages from the team to this device.
  - Website / installed web app: web push (ported from Tazkiyah's usePush), registered with the
    server together with the reminder choice so the server can send lesson reminders too.
  - Phone app: Firebase token through Capacitor's PushNotifications plugin (works once the
    Firebase files are added to native/). Lesson alarms do not need it; they are booked on-device.
*/
import { API_BASE } from "app/services/index";
import type { ReminderPrefs } from "app/services/types";
import storage, { STORAGE_KEYS } from "app/utils/storage";
import { deviceLabel, isApple, isNativeApp, isStandalone, nativePlatform, nativePlugin } from "./nativeApp";

export type PushSupport = "ready" | "install-first" | "blocked" | "unsupported" | "native";

export function pushSupport(): PushSupport {
  if (isNativeApp()) return "native";
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return "unsupported";
  if (isApple() && !isStandalone()) return "install-first";
  if (!("PushManager" in window) || !("Notification" in window)) return "unsupported";
  if (Notification.permission === "denied") return "blocked";
  return "ready";
}

const timeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/London";

function keyBytes(base64: string): Uint8Array {
  const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
}

async function post(path: string, body: unknown) {
  const token = storage.getItem(STORAGE_KEYS.TOKEN);
  const res = await fetch(`${API_BASE}/api${path}`, { method: "POST", headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error || `HTTP ${res.status}`);
  return res.json();
}

/** Web: subscribe (or re-subscribe) this browser and tell the server. Permission must already be granted. */
export async function registerWebPush(publicKey: string, reminders: ReminderPrefs) {
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  const stored = storage.getItem(STORAGE_KEYS.PUSH_KEY) ?? "";
  if (sub && stored && stored !== publicKey) { await sub.unsubscribe(); sub = null; }
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) as BufferSource });
  storage.setItem(STORAGE_KEYS.PUSH_KEY, publicKey);
  await post("/devices", { subscription: sub.toJSON(), platform: "web", label: deviceLabel(), timeZone: timeZone(), reminders });
  return sub;
}

export async function currentWebSubscription(): Promise<PushSubscription | null> {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

export async function unregisterWebPush() {
  const sub = await currentWebSubscription();
  if (sub) { await post("/devices/remove", { endpoint: sub.endpoint }).catch(() => undefined); await sub.unsubscribe(); }
}

/* ---- phone app (Firebase through Capacitor) ---- */
interface PushPlugin {
  checkPermissions(): Promise<{ receive: string }>;
  requestPermissions(): Promise<{ receive: string }>;
  register(): Promise<void>;
  addListener(event: string, fn: (data: any) => void): Promise<{ remove: () => void }> | { remove: () => void };
}

let phoneToken: string | null = null;
export const getPhoneToken = () => phoneToken;

/** Phone: ask for permission, get the Firebase token and register it. Resolves false when push is not set up in this build. */
export async function registerPhonePush(reminders: ReminderPrefs): Promise<boolean> {
  const plugin = nativePlugin<PushPlugin>("PushNotifications");
  if (!plugin) return false;
  const perm = await plugin.requestPermissions().catch(() => ({ receive: "denied" }));
  if (perm.receive !== "granted") return false;
  return new Promise<boolean>((resolve) => {
    const done = (ok: boolean) => { clearTimeout(timer); resolve(ok); };
    const timer = setTimeout(() => done(false), 8000);
    void plugin.addListener("registration", async (t: { value: string }) => {
      phoneToken = t.value;
      try { await post("/devices", { token: t.value, platform: nativePlatform(), label: deviceLabel(), timeZone: timeZone(), reminders }); done(true); } catch { done(false); }
    });
    void plugin.addListener("registrationError", () => done(false));
    plugin.register().catch(() => done(false));
  });
}

/** Keep the server's copy of the reminder choice for this device in step. */
export async function syncDeviceReminders(reminders: ReminderPrefs) {
  try {
    if (isNativeApp()) { if (phoneToken) await post("/devices", { token: phoneToken, platform: nativePlatform(), label: deviceLabel(), timeZone: timeZone(), reminders }); return; }
    const sub = await currentWebSubscription();
    if (sub) await post("/devices", { subscription: sub.toJSON(), platform: "web", label: deviceLabel(), timeZone: timeZone(), reminders });
  } catch { /* offline: synced next time */ }
}
